import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RotateCcw, Send, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../../context/LanguageContext'
import { chatbotApi } from '../../services/api'
import MayarAvatar from './MayarAvatar'

const STORAGE_KEY = 'mayar_chatbot_conversation_id'

export default function ChatbotWidget() {
  const { t } = useTranslation()
  const { language, isRTL } = useLanguage()
  const titleId = useId()
  const panelRef = useRef(null)
  const listRef = useRef(null)
  const inputRef = useRef(null)

  const [open, setOpen] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [conversationId, setConversationId] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '')

  useEffect(() => {
    chatbotApi
      .config()
      .then(({ data }) => {
        const payload = data.data || data
        setEnabled(Boolean(payload.enabled))
        setSuggestions(payload.suggestions || [])
      })
      .catch(() => setEnabled(false))
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages, loading, open])

  if (!enabled) return null

  const welcome = t('chatbot.welcome')

  const resetConversation = () => {
    sessionStorage.removeItem(STORAGE_KEY)
    setConversationId('')
    setMessages([])
    setError('')
    setInput('')
  }

  const sendMessage = async (rawText) => {
    const text = String(rawText || '').trim()
    if (!text || loading) return

    setError('')
    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: text }])
    setLoading(true)

    try {
      const { data } = await chatbotApi.message({
        message: text,
        language,
        conversation_id: conversationId || undefined,
      })
      if (data.conversation_id) {
        setConversationId(data.conversation_id)
        sessionStorage.setItem(STORAGE_KEY, data.conversation_id)
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.message,
          actions: data.actions || [],
        },
      ])
    } catch (err) {
      const apiMessage = err?.response?.data?.message
      setError(apiMessage || t('chatbot.unavailable'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    sendMessage(input)
  }

  return (
    <>
      <div className="fixed bottom-[max(5.5rem,calc(env(safe-area-inset-bottom)+5.5rem))] end-4 z-40 flex flex-col items-center gap-1.5 sm:bottom-28 sm:end-6">
        {!open ? (
          <span className="max-w-[5.5rem] rounded-full bg-white/95 px-2 py-0.5 text-center text-[10px] font-semibold leading-tight text-primary shadow-sm ring-1 ring-primary/15 sm:max-w-[6.5rem] sm:text-[11px]">
            {t('chatbot.fabLabel')}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-label={open ? t('chatbot.close') : t('chatbot.open')}
          aria-expanded={open}
          aria-controls={open ? titleId : undefined}
          className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-white shadow-lg shadow-primary/25 ring-2 ring-primary/20 transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:h-[3.75rem] sm:w-[3.75rem]"
        >
          {open ? (
            <span className="flex h-full w-full items-center justify-center bg-primary text-white">
              <X className="h-6 w-6" />
            </span>
          ) : (
            <MayarAvatar className="h-full w-full" title={t('chatbot.title')} />
          )}
        </button>
      </div>

      {open ? (
        <section
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          dir={isRTL ? 'rtl' : 'ltr'}
          className="fixed inset-x-0 bottom-0 z-50 flex h-[min(100dvh,100%)] flex-col bg-white shadow-2xl sm:inset-x-auto sm:bottom-28 sm:end-6 sm:h-[min(34rem,calc(100dvh-8rem))] sm:w-[24rem] sm:rounded-2xl sm:border sm:border-border"
        >
          <header className="flex items-center gap-3 border-b border-border bg-primary px-4 py-3 text-white">
            <div className="h-10 w-10 overflow-hidden rounded-full ring-2 ring-white/30">
              <MayarAvatar className="h-full w-full" title={t('chatbot.title')} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="truncate text-sm font-semibold">
                {t('chatbot.title')}
              </h2>
              <p className="text-xs text-white/80">{t('chatbot.online')}</p>
            </div>
            <button
              type="button"
              onClick={resetConversation}
              className="rounded-lg p-2 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label={t('chatbot.reset')}
              title={t('chatbot.reset')}
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-2 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label={t('chatbot.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-background px-4 py-4">
            <div className="rounded-2xl bg-white px-3 py-2 text-sm text-text shadow-sm">
              {welcome}
            </div>

            {messages.length === 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-text/60">{t('chatbot.suggestions')}</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => sendMessage(language === 'ar' ? item.topic_ar : item.topic_en)}
                      className="rounded-full border border-border bg-white px-3 py-1.5 text-start text-xs text-primary transition hover:border-primary/40 hover:bg-primary/5"
                    >
                      {language === 'ar' ? item.topic_ar : item.topic_en}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                    message.role === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-white text-text'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  {message.actions?.length ? (
                    <div className="mt-2 flex flex-col gap-1">
                      {message.actions.map((action) => (
                        <Link
                          key={action.url}
                          to={action.url}
                          onClick={() => setOpen(false)}
                          className="rounded-lg bg-primary/10 px-2 py-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
                        >
                          {action.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            {loading ? (
              <p className="text-xs text-text/50" aria-live="polite">
                {t('chatbot.typing')}
              </p>
            ) : null}

            {error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                <p>{error}</p>
                <button
                  type="button"
                  className="mt-2 text-xs font-semibold underline"
                  onClick={() => {
                    const lastUser = [...messages].reverse().find((item) => item.role === 'user')
                    if (lastUser) sendMessage(lastUser.content)
                  }}
                >
                  {t('chatbot.retry')}
                </button>
              </div>
            ) : null}
          </div>

          <form onSubmit={handleSubmit} className="border-t border-border bg-white p-3">
            <label htmlFor="chatbot-input" className="sr-only">
              {t('chatbot.placeholder')}
            </label>
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                id="chatbot-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    sendMessage(input)
                  }
                }}
                placeholder={t('chatbot.placeholder')}
                className="max-h-28 min-h-[2.75rem] flex-1 resize-none rounded-xl border border-border px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                maxLength={1000}
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                aria-label={t('chatbot.send')}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white transition hover:bg-primary/90 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </section>
      ) : null}
    </>
  )
}
